import * as React from 'react';
import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { DataGridPremium, GridRow, useGridApiRef } from '@mui/x-data-grid-premium';

import { CustomFooter } from 'components/pages/finance/shared/FinanseTableMUI/CustomFooter/CustomFooter';
import { CustomToolbar } from 'components/pages/finance/shared/FinanseTableMUI/CustomToolbar/CustomToolbar';

export const FinanceTableMUI = ({
  getTogglableColumns,
  footerElementsCount,
  sx,
  initialState,
  localStorageTableName,
  columns,
  tableVersion,
  columnsFilterSort,
  'data-testid': dataTestId,
  customCsvOptions,
  ...otherProps
}) => {
  const apiRef = useGridApiRef();

  const [savedState, setSavedState] = useState();
  const [quickFilterExpanded, setQuickFilterExpanded] = useState(false);
  const localStorageStateName = useMemo(() => `${localStorageTableName}_TABLE_CONFIGURATION`, [localStorageTableName]);

  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem(localStorageStateName, JSON.stringify({ ...currentState }));
    }
  }, [apiRef, localStorageStateName]);

  useLayoutEffect(() => {
    const savedStateVersion = localStorage?.getItem(`${localStorageTableName}_TABLE_VERSION`);
    if (savedStateVersion !== tableVersion) {
      localStorage.removeItem(localStorageStateName);
      localStorage.removeItem(`${localStorageTableName}_TABLE_CUSTOM_VIEW`);
      localStorage.setItem(`${localStorageTableName}_TABLE_VERSION`, tableVersion);
    }
  }, [localStorageStateName, localStorageTableName, tableVersion]);

  useLayoutEffect(() => {
    const stateFromLocalStorage = localStorage?.getItem(localStorageStateName);
    const parsedState = stateFromLocalStorage ? JSON.parse(stateFromLocalStorage) : {};
    setSavedState(parsedState);
    if (parsedState?.filter?.filterModel?.quickFilterValues?.length > 0) {
      setQuickFilterExpanded(true);
    } else {
      setQuickFilterExpanded(false);
    }
    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [localStorageStateName, saveSnapshot]);

  if (!savedState) {
    return null;
  }

  return (
    <DataGridPremium
      {...otherProps}
      columns={columns}
      apiRef={apiRef}
      onStateChange={saveSnapshot}
      initialState={{
        ...initialState,
        ...savedState
      }}
      disableRowGrouping
      disableAggregation
      disablePivoting
      rowHeight={38}
      slots={{
        toolbar: CustomToolbar,
        footer: CustomFooter,
        row: CustomRow
      }}
      slotProps={{
        root: {
          'data-testid': dataTestId
        },
        columnsManagement: {
          getTogglableColumns,
          sort: columnsFilterSort
        },
        filterPanel: {
          columnsSort: columnsFilterSort
        },
        toolbar: {
          'data-testid': `finance-table-toolbar`,
          localStorageTableName,
          printOptions: { disableToolbarButton: true },
          excelOptions: { disableToolbarButton: true },
          csvOptions: {
            utf8WithBom: true,
            escapeFormulas: false,
            ...customCsvOptions
          },
          quickFilterProps: { expanded: quickFilterExpanded, onExpandedChange: setQuickFilterExpanded }
        },
        footer: {
          count: footerElementsCount
        }
      }}
      sx={{
        padding: '20px 40px 0 40px',
        border: 'none',
        ...sx
      }}
      showToolbar
      disableRowSelectionExcludeModel
    />
  );
};

export const CustomRow = props => {
  return <GridRow {...props} data-testid="finance-table-row" />;
};
